/**
 * compileCharacter: gear + gems + talents → a patched ClassDef with final stats (PLAN §11
 * "Architecture"). The sim never learns about items or talents; see inject.ts for how the
 * result is fed to createSim.
 *
 * Pure and deterministic: the inputs are never mutated (transforms run on a deep clone) and the
 * output depends only on the inputs and the static data tables.
 *
 * Transform order: talents (tree order) → tier-set bonuses (sets.ts) → legendaries → trinkets →
 * gem bonuses. Later transforms
 * see earlier ones' output, so e.g. a gem bonus can proc off a talent-added aura.
 *
 * Stats (what gear does):
 *  - Main stat. power(avgIlvl) = POWER_PER_ILVL^(avgIlvl − 200) (items.ts) multiplies Stats.power (every damage,
 *    heal and shield coefficient) and max HP. Empty slots count as ilvl 180 (items.ts).
 *  - Secondaries. Item rating → fraction via items.secondaryFractions (RATING_PER_PERCENT rating
 *    = 1%), then the SECONDARY_* constants below:
 *      haste     → Stats.haste (GCD, casts, swings, hasted ticks)
 *      crit      → Stats.crit (crits deal ×2)
 *      expertise → Stats.mastery, and scales the class's ClassStatProfile.expertise mods
 *                  (e.g. Warden: Bulwark Strike healing and damage taken; Stormcaller: nature damage)
 *      spirit    → cooldownRatePct on every ability, plus a chance on each resource-costing cast
 *                  to refund part of the cost
 *    Example: a full ilvl-270 set rolled haste/crit has ~336 rating of each → +16.8% haste/crit.
 */
import type { AbilityDef, AuraDef, ClassDef, ProcSpec, StatMod, Stats } from '@keystone/sim';
import { CLASS_STAT_PROFILES, legendary, trinketEffect } from './data';
import { activeGemBonuses, gemPower } from './gems';
import { averageIlvl, powerMult, secondaryFractions } from './items';
import { setTransforms } from './sets';
import { talentTree, validateLoadout } from './talents';
import { applyTransforms } from './transforms';
import { applyGrid } from './grid.compile'; // +grid
import type { ClassTransform, CompiledCharacter, EquippedGear, SecondaryFractions, TalentId, TalentLoadout } from './types';
import { ITEM_SLOTS } from './types';

export const EXPERTISE_AURA_ID = (classId: string): string => `progression.${classId}.expertise`;
export const SPIRIT_AURA_ID = (classId: string): string => `progression.${classId}.spirit`;

/** Secondary fraction → Stats.haste. */
export const SECONDARY_HASTE_PER_FRACTION = 1;
/** Secondary fraction → Stats.crit. */
export const SECONDARY_CRIT_PER_FRACTION = 1;
/** Secondary fraction → Stats.mastery and the multiplier on ClassStatProfile.expertise mods. */
export const SECONDARY_EXPERTISE_PER_FRACTION = 1;
/** Spirit fraction → cooldownRatePct (0.1 spirit = cooldowns recover 10% faster). */
export const SPIRIT_COOLDOWN_RATE_PER_FRACTION = 1;
/** Spirit fraction → chance that a resource-costing cast refunds part of its cost. */
export const SPIRIT_REFUND_CHANCE_PER_FRACTION = 1;
/** Share of the cost a spirit refund gives back (at least 1 point of the resource). */
export const SPIRIT_REFUND_SHARE = 0.5;

export interface CompileOptions {
  /**
   * Skip the structural loadout check (unknown or other-class nodes, prerequisites, tier gates,
   * capstone conflicts). Default false: an invalid loadout throws. Budget (hero level) and rank
   * gating are NOT checked here; that is the profile's job (validateLoadout with real points).
   */
  trustTalents?: boolean;
  /**
   * +grid: Waystone Grid stars to compile in (grid.compile.ts applyGrid), applied to the spec'd,
   * talent- and gear-patched ClassDef so slot modifiers see the final binds. Trusted as given:
   * callers pass sanitizeGrid / suggestedGrid output.
   */
  grid?: readonly string[];
}

export function compileCharacter(
  classDef: ClassDef,
  gear: EquippedGear,
  talents: TalentLoadout | readonly TalentId[],
  opts: CompileOptions = {},
): CompiledCharacter {
  const classId = classDef.id;
  const nodeIds: readonly TalentId[] = isLoadout(talents) ? talents.nodes : talents;
  const tree = talentTree(classId);
  const picked = new Set(nodeIds);
  for (const id of picked) if (!tree.some((n) => n.id === id)) throw new Error(`compileCharacter: ${id} is not a ${classId} talent`);
  if (!opts.trustTalents) {
    const errors = validateLoadout({ id: 'compile', name: 'compile', classId, nodes: [...nodeIds] }, Number.POSITIVE_INFINITY, 3);
    if (errors.length) throw new Error(`compileCharacter: invalid ${classId} loadout: ${errors.map((e) => `${e.kind}(${e.kind === 'overBudget' ? e.spent : e.nodeId})`).join(', ')}`);
  }
  const talentNodes = tree.filter((n) => picked.has(n.id));

  // ---- transforms ------------------------------------------------------------
  const transforms: ClassTransform[] = [];
  for (const n of talentNodes) transforms.push(...n.effect);
  const setResult = setTransforms(classDef, gear);
  transforms.push(...setResult.transforms);
  const legendaries: string[] = [];
  const trinkets: string[] = [];
  for (const slot of ITEM_SLOTS) {
    const it = gear[slot];
    if (it?.legendaryId) {
      const l = legendary(it.legendaryId);
      if (l && l.classId === classId && !legendaries.includes(l.id)) {
        transforms.push(...l.effect);
        legendaries.push(l.id);
      }
    }
    if (it?.trinketEffectId) {
      const t = trinketEffect(it.trinketEffectId);
      if (t && (!t.roles || t.roles.length === 0 || t.roles.includes(classDef.role)) && !trinkets.includes(t.id)) {
        transforms.push(...t.effect);
        trinkets.push(t.id);
      }
    }
  }
  const power = gemPower(gear);
  const bonuses = activeGemBonuses(power, classId);
  for (const b of bonuses) transforms.push(...b.effect);

  const registerAuras: AuraDef[] = [];
  for (const t of transforms) if (t.op === 'registerAura' && !registerAuras.some((a) => a.id === t.aura.id)) registerAuras.push(structuredClone(t.aura));

  const passivesBefore = new Set((classDef.passives ?? []).map((a) => a.id));
  let patched = applyTransforms(classDef, transforms);
  if (opts.grid?.length) patched = applyGrid(patched, opts.grid); // +grid

  // ---- stats -----------------------------------------------------------------
  const avgIlvl = averageIlvl(gear);
  const mult = powerMult(avgIlvl);
  const sec = secondaryFractions(gear);
  const stats = statsFor(classDef.baseStats, mult, sec);
  const maxHp = Math.round(classDef.maxHp * mult);
  patched.baseStats = stats;
  patched.maxHp = maxHp;

  // ---- secondary auras (expertise is class-specific; spirit = cooldowns + refunds) ----
  const statAuras: AuraDef[] = [];
  const profile = CLASS_STAT_PROFILES[classId];
  const expertise = sec.expertise * SECONDARY_EXPERTISE_PER_FRACTION;
  if (profile && expertise > 0) {
    const mods: StatMod[] = profile.expertise.map((m) => ({ ...m, value: m.value * expertise }));
    statAuras.push(hiddenPassive(EXPERTISE_AURA_ID(classId), 'Expertise', { mods }));
  }
  if (sec.spirit > 0) statAuras.push(spiritAura(classId, sec.spirit, patched.abilities));
  patched.passives = [...(patched.passives ?? []), ...statAuras];

  // Everything progression added must survive death: the sim applies passives only at spawn.
  const extraAuras: AuraDef[] = [];
  for (const a of patched.passives) {
    if (passivesBefore.has(a.id)) continue;
    a.flags = { ...a.flags, persistThroughDeath: true };
    extraAuras.push(structuredClone(a));
  }

  return {
    classId,
    classDef: patched,
    stats: { ...stats },
    maxHp,
    extraAuras,
    registerAuras,
    sheet: {
      avgIlvl,
      powerMult: mult,
      secondaries: sec,
      gemPower: power,
      gemBonuses: bonuses.map((b) => b.id),
      talents: talentNodes.map((n) => n.id),
      legendaries,
      trinkets,
      sets: setResult.sets,
      ...(opts.grid?.length ? { grid: [...opts.grid] } : {}), // +grid
    },
  };
}

/** Final unbuffed stats from the class base, the main-stat multiplier and secondary fractions. */
export function statsFor(base: Stats, mainStatMult: number, sec: SecondaryFractions): Stats {
  return {
    ...base,
    power: Math.round(base.power * mainStatMult),
    haste: base.haste + sec.haste * SECONDARY_HASTE_PER_FRACTION,
    crit: base.crit + sec.crit * SECONDARY_CRIT_PER_FRACTION,
    mastery: base.mastery + sec.expertise * SECONDARY_EXPERTISE_PER_FRACTION,
  };
}

/** Spirit: faster cooldowns, and a refund proc for every ability that costs a resource. */
function spiritAura(classId: string, spirit: number, abilities: readonly AbilityDef[]): AuraDef {
  const chance = Math.min(1, spirit * SPIRIT_REFUND_CHANCE_PER_FRACTION);
  const procs: ProcSpec[] = [];
  for (const ab of abilities) {
    for (const c of ab.cost ?? []) {
      if (c.amount <= 0) continue;
      procs.push({
        on: 'cast',
        abilityIds: [ab.id],
        chance,
        effects: [{ type: 'resource', to: 'self', resource: c.resource, amount: Math.max(1, Math.floor(c.amount * SPIRIT_REFUND_SHARE)) }],
      });
    }
  }
  return hiddenPassive(SPIRIT_AURA_ID(classId), 'Spirit', {
    mods: [{ stat: 'cooldownRatePct', value: spirit * SPIRIT_COOLDOWN_RATE_PER_FRACTION }],
    ...(procs.length ? { procs } : {}),
  });
}

function hiddenPassive(id: string, name: string, extra: Partial<AuraDef>): AuraDef {
  return { id, name, kind: 'buff', durationMs: 0, stacking: 'unique', flags: { hidden: true, persistThroughDeath: true }, ...extra };
}

function isLoadout(t: TalentLoadout | readonly TalentId[]): t is TalentLoadout {
  return !Array.isArray(t);
}
